import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { UserSurvey } from "@/components/widgets/UserSurvey";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { FormType, UserStatus } from "@/pages/DashboardPage/types";
import { PhoneNumberForm } from "@/shared/components/PhoneNumberForm";
import { FirstMessage } from "@/shared/components/userFlow/FirstMessage";
import { MessageForm } from "@/shared/components/userFlow/MessageForm";
import { SuccessMessage } from "@/shared/components/userFlow/SuccessMessage";
import { UpdatedPlanMessage } from "@/shared/components/userFlow/UpdatedPlanMessage";
import { MODAL_BACKGROUND_STYLE } from "@/shared/constants/customStyleSets";

type Props = {
	userStatus: UserStatus | null;
	setUserStatus: React.Dispatch<React.SetStateAction<UserStatus | null>>;
};

const formFlows: Record<UserStatus, FormType[]> = {
	registered: ["welcome", "survey", "success"],
	upgradedPlan: ["phone", "confirmedPhoneMsg"],
	notConfirmedPhone: ["phone", "confirmedPhoneMsg"],
	unAuthenticatedAttemptedToBuy: [
		"phone",
		"confirmedPhoneMsg",
		"survey",
		"success",
	],
};

type FormComponents = {
	[key in FormType]?: React.ReactElement;
};

export const FormRenderer: React.FC<Props> = (props) => {
	const { userStatus, setUserStatus } = props;
	const [currentForm, setCurrentForm] = useState(0);
	const [searchParams, setSearchParams] = useSearchParams();
	const [, setAttemptToBuy] = useLocalStorage<boolean | null>(
		"attemptToBuy",
		null,
	);

	const nextForm = useCallback(() => {
		if (!userStatus) return;

		if (currentForm < formFlows[userStatus].length - 1) {
			setCurrentForm((prev) => prev + 1);
		} else {
			setUserStatus(null);
			setCurrentForm(0);
		}
	}, [userStatus, currentForm, setUserStatus]);

	const formComponents: FormComponents = useMemo(
		() => ({
			welcome: (
				<MessageForm
					message={<FirstMessage />}
					onSubmit={nextForm}
					buttonText="Complete Profile"
				/>
			),
			survey: <UserSurvey onSuccess={nextForm} />,
			phone: (
				<PhoneNumberForm
					onSuccess={nextForm}
					onClose={() => setUserStatus(null)}
				/>
			),
			confirmedPhoneMsg: (
				<MessageForm
					message={<UpdatedPlanMessage />}
					onSubmit={() => {
						nextForm();
						const params = new URLSearchParams(searchParams);
						params.delete("redirect");
						setSearchParams(params);
						setAttemptToBuy(null);
					}}
					buttonText="Let's Go"
				/>
			),
			success: (
				<MessageForm
					message={<SuccessMessage />}
					onSubmit={() => setUserStatus(null)}
					buttonText="Let's Go"
				/>
			),
		}),
		[nextForm, searchParams, setSearchParams, setAttemptToBuy, setUserStatus],
	);

	const formType =
		userStatus &&
		userStatus in formFlows &&
		currentForm < formFlows[userStatus].length
			? formFlows[userStatus][currentForm]
			: undefined;

	const CurrentForm = formType ? formComponents[formType] : undefined;

	return CurrentForm ? (
		<div className={MODAL_BACKGROUND_STYLE}>{CurrentForm}</div>
	) : null;
};
